import axios from './axiosConfig';
import { environment } from '../config/environment';
import { ISignageFile, ISignageItem, ISignageItemRequest, ISignageStore, ISignageStoreSettings, SignageMode, SignageState } from '../types/signage';

const base = `${environment.apiUrl}signage`;

// A server that doesn't know a route can answer 200 with its index.html (SPA
// fallback), which axios hands over as a string. Fail with a clear message
// instead of letting a string flow into the page.
const NOT_AVAILABLE = 'Signage is not available on this server (unexpected response). The backend may need to be updated.';

const expectArray = <T>(data: unknown): T[] => {
  if (!Array.isArray(data)) throw NOT_AVAILABLE;
  return data as T[];
};

const expectObject = <T>(data: unknown): T => {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) throw NOT_AVAILABLE;
  return data as T;
};

export const signageService = {
  getStores: async (): Promise<ISignageStore[]> => {
    const response = await axios.get<unknown>(`${base}/store`);
    return expectArray<ISignageStore>(response.data);
  },

  createStore: async (storeId: string, name: string): Promise<void> => {
    await axios.post(`${base}/store`, { storeId, name });
  },

  // The Default store keeps its name: only its settings are saved.
  updateStore: async (storeId: string, name: string, settings?: ISignageStoreSettings): Promise<void> => {
    await axios.put(`${base}/store/${encodeURIComponent(storeId)}`, { name, ...settings });
  },

  deleteStore: async (storeId: string): Promise<void> => {
    await axios.delete(`${base}/store/${encodeURIComponent(storeId)}`);
  },

  getItems: async (storeId: string): Promise<ISignageItem[]> => {
    const response = await axios.get<unknown>(`${base}/item`, { params: { storeId } });
    return expectArray<ISignageItem>(response.data);
  },

  createItem: async (request: ISignageItemRequest): Promise<ISignageItem> => {
    const response = await axios.post<unknown>(`${base}/item`, request);
    return expectObject<ISignageItem>(response.data);
  },

  updateItem: async (id: string, request: ISignageItemRequest): Promise<ISignageItem> => {
    const response = await axios.put<unknown>(`${base}/item/${id}`, request);
    return expectObject<ISignageItem>(response.data);
  },

  setItemEnabled: async (id: string, enabled: boolean): Promise<void> => {
    await axios.patch(`${base}/item/${id}`, { enabled });
  },

  deleteItem: async (id: string): Promise<void> => {
    await axios.delete(`${base}/item/${id}`);
  },

  setOrder: async (storeId: string, mode: SignageMode, state: SignageState, ids: string[]): Promise<void> => {
    await axios.put(`${base}/item/order`, { storeId, mode, state, ids });
  },

  uploadFile: async (file: File): Promise<ISignageFile> => {
    const form = new FormData();
    form.append('file', file);
    const response = await axios.post<unknown>(`${base}/file`, form);
    return expectObject<ISignageFile>(response.data);
  },

  // Files are served behind the bearer token, so previews fetch a blob.
  getFileBlob: async (fileId: string): Promise<Blob> => {
    const response = await axios.get<Blob>(`${base}/file/${fileId}`, { responseType: 'blob' });
    return response.data;
  },
};
